import { BathIcon, BedIcon, CheckmarkCircleIcon, EditIcon } from '@calizahq/react-components-v2'
import { getNMTLinkQueryParams, getPDPLinkQueryParams } from '@sales-app/context/workspace/homeFilters.util'
import { HomeFilters } from '@sales-app/context/workspace/workspace.type'
import { RecommendedHomeRow } from '@sales-app/hooks/useFavoritesPinnedHomes/useFavoritesPinnedHomes.util'
import { ReservationType } from '@sales-app/views/PotentialBooking/PotentialBooking/ControlSection/Filters/ReservationTypeSelection'
import clsx from 'clsx'
import React, { useCallback, useEffect, useState } from 'react'

import { ReactComponent as LinkIcon } from '@/assets/icons/link.svg'
import { Tooltip } from '@/components'
import { useAlertContext } from '@/context'
import { useMobile } from '@/hooks/useMobile'
import { useScrollLock } from '@/hooks/useScrollLock'
import { formatDate, parseDate } from '@/util'

import { HomeListItemImage } from './HomeListItemImage'
import { HomeListWrapper } from './MapPopup.styles'
import { formatMoney, getHomesTabs, transformToBedroomText } from './MapPopup.util'

function formatBathroomLabel(bathCount: string): string {
  return bathCount.includes('.5') ? `${bathCount} Bath` : `${parseInt(bathCount, 10)} Bath`
}

export function HomeList({
  homes,
  filters,
}: {
  filters: HomeFilters
  homes: RecommendedHomeRow[]
  propertyId: number
}): JSX.Element {
  const { moveInStartDate: startDate } = filters
  const { isMobile } = useMobile()
  const isStandbyPage = false
  const homeTabsData = getHomesTabs(homes)
  const { unlockScroll } = useScrollLock()
  const [selectedType, setSelectedType] = useState<{ data?: RecommendedHomeRow[]; title?: string }[]>(homeTabsData)
  const { setAlert } = useAlertContext()
  const { reservationType } = filters
  const PAGE_URL = process.env.PAGE_URL
  const ADMIN_URL = process.env.ADMIN_URL

  const handleClickHome = useCallback(
    (ev, _index: number) => {
      ev.preventDefault()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isMobile, unlockScroll],
  )

  useEffect(() => {
    if (selectedType.length === 0 && homeTabsData.length > 0) {
      setSelectedType(homeTabsData)
    }
  }, [homeTabsData, selectedType.length])

  function toggleOption(title: string | undefined, data: RecommendedHomeRow[]) {
    if (checkOption(title)) {
      let selectedTypecopy = [...selectedType]

      selectedTypecopy.splice(
        selectedTypecopy.findIndex(function (i) {
          return i.title === title
        }),
        1,
      )

      setSelectedType(selectedTypecopy)
    } else {
      const selectedObj = {
        data,
        title,
      }
      setSelectedType([...selectedType, selectedObj])
    }

    if (selectedType.length === 0) {
      setSelectedType(homeTabsData)
    }
  }

  function checkOption(title?: string) {
    return selectedType.some((el) => el.title === title)
  }

  function getLowestPrice(arr) {
    const sortedPriceObj = arr.reduce(function (prev, curr) {
      return prev.display_price_with_discount < curr.display_price_with_discount ? prev : curr
    })

    return sortedPriceObj.display_price_with_discount
  }
  const buttonClassName = 'grid justify-center items-center w-4 h-4 mr-2'

  const getPDPLink = (home: RecommendedHomeRow) => {
    const queryParams = getPDPLinkQueryParams(filters, home)

    return reservationType === ReservationType.Standby
      ? `${PAGE_URL}/homes/${home.slug}/standby${queryParams}`
      : `${PAGE_URL}/homes/${home.slug}${queryParams}`
  }

  const openPDP = (home) => {
    const url = getPDPLink(home)
    window.open(url, '_blank')
  }
  const copySharedLink = (home) => {
    const url = getPDPLink(home)
    navigator.clipboard.writeText(url)
    setAlert('Shared link copied', 'success')
  }
  const routeToNMTReservationTool = (home: RecommendedHomeRow) => {
    const queryParams = getNMTLinkQueryParams(filters, home)
    if (!queryParams) return
    const url = `${ADMIN_URL}/admin/reservation-booker${queryParams}`
    window.open(url, '_blank')
  }
  return (
    <div className="flex flex-col justify-center">
      <div className="w-full px-2 py-3">
        <div className="flex">
          {homeTabsData.map(({ data, title }, index) => {
            const selected = checkOption(title)
            return (
              <button
                className={clsx(
                  'relative z-10 hover:z-20 last-of-type:ml-1 first-of-type:mr-1 mx-1 px-3 py-2 text-left font-bold text-sm border rounded-xl focus:outline-none disabled:cursor-default',
                  'line-height-100',
                  selected ? 'text-black bg-grey-200 border-grey-200' : 'text-tertiary bg-white border-grey-400',
                )}
                key={title + index}
                onClick={() => toggleOption(title, data)}
                style={{ width: 'calc(100% / 3)' }}
              >
                {' '}
                <p className="font-bold text-p-sm leading-4">{title}</p>
                {!isStandbyPage && (
                  <p className="font-medium text-label-xs leading-3 text-tertiary">${getLowestPrice(data)}+</p>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {selectedType?.length > 0 && (
        <div className="pb-2">
          {selectedType.map((home, index) => {
            const { data, title } = home

            return (
              data &&
              title && (
                <div key={title + index}>
                  {data.map((home: RecommendedHomeRow, _index) => {
                    let availableStartDate = home?.start_date_or_first_available_start_date
                      ? formatDate(parseDate(home?.start_date_or_first_available_start_date), 'MM/dd')
                      : ''

                    const priceWithoutDiscount =
                      home.display_price_without_discount && typeof home.display_price_without_discount === 'string'
                        ? parseInt(home.display_price_without_discount)
                        : home.display_price_without_discount

                    return (
                      <HomeListWrapper
                        className="flex items-center justify-between"
                        data-testid="home-list-link"
                        href={''}
                        key={home.id}
                        onClick={(ev) => handleClickHome(ev, _index)}
                        rel="noreferrer noopener"
                        target="_blank"
                      >
                        <div className="mx-2 mb-2 flex w-full rounded-xl border border-grey-300">
                          <div className="min-w-16 flex-grow" onClick={() => openPDP(home)} role="presentation">
                            <HomeListItemImage image={home.home_image as string} />
                          </div>
                          <div className="flex flex-row flex-wrap p-3 pl-3">
                            <div className="flex w-full flex-grow items-baseline justify-between border-b border-grey-300 pb-1">
                              <div className="text-bold mr-1 text-sm">#{home.unit_number}</div>
                              <div className="flex flex-wrap items-center justify-end">
                                <div className="flex items-center">
                                  <BedIcon className="text-secondary" width={12} />
                                  <span className="whitespace-nowrap pl-1 font-medium text-sm text-secondary">
                                    {transformToBedroomText(home.bedrooms)}
                                  </span>
                                </div>
                                <div className="flex items-center pl-3">
                                  <BathIcon className="text-secondary" width={11} />
                                  <span className="whitespace-nowrap pl-1 font-medium text-sm text-secondary">
                                    {formatBathroomLabel(home.baths)}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex w-full items-baseline justify-between pt-1">
                              {startDate && !isStandbyPage && (
                                <div className="flex items-baseline">
                                  {home.special_offer && home.display_price_with_discount !== priceWithoutDiscount ? (
                                    <div>
                                      <span className="text-sm text-grey-500 line-through">
                                        {formatMoney(home.display_price_without_discount, true)}&nbsp;
                                      </span>
                                      <span className="font-bold text-sm text-black">
                                        {formatMoney(home.display_price_with_discount, true)}
                                      </span>
                                      <span className="font-medium text-p-xs text-black">
                                        {home.display_price_label}
                                      </span>
                                    </div>
                                  ) : (
                                    <div className="flex items-baseline">
                                      <div className="font-medium text-sm text-black">
                                        {formatMoney(home.display_price_without_discount, true)}
                                      </div>
                                      <div className="pb-1 font-medium text-p-xs text-black">
                                        {home.display_price_label}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                              {!startDate && !isMobile && !isStandbyPage && (
                                <div
                                  className="text-green-700 whitespace-pre font-bold text-sm transition-opacity duration-150 ease-in hover:opacity-50"
                                  data-testid="home-list-option-show-rate"
                                >
                                  Show Rate
                                </div>
                              )}
                              {availableStartDate && (
                                <div className="flex">
                                  <span className="text-sm text-tertiary">Available&nbsp;</span>
                                  <span className="text-bold text-sm text-black">{availableStartDate}</span>
                                </div>
                              )}
                            </div>
                            <div className="flex pt-2">
                              <button className={buttonClassName} onClick={() => copySharedLink(home)}>
                                <LinkIcon className="w-4 cursor-pointer text-green-300" />
                              </button>
                              <button className={buttonClassName} onClick={() => routeToNMTReservationTool(home)}>
                                <EditIcon className="w-4 cursor-pointer text-green-300" />
                              </button>
                              {home.installed && (
                                <Tooltip text="Installed">
                                  <BedIcon className="text-orange-700 w-4 " />
                                </Tooltip>
                              )}

                              {home.property_approved && (
                                <Tooltip text="Corporate approved">
                                  <CheckmarkCircleIcon className="ml-3 w-4 " />
                                </Tooltip>
                              )}
                            </div>
                          </div>
                        </div>
                      </HomeListWrapper>
                    )
                  })}
                </div>
              )
            )
          })}
        </div>
      )}
    </div>
  )
}
